import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  LinearProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import {
  Build as BuildIcon,
  Warning as EmergencyIcon,
  Storage as StorageIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { SocketContext } from "../../../../context/Socket/SocketContext";
import { AuthContext } from "../../../../context/Auth/AuthContext";
import api from "../../../../services/api";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: theme.spacing(3),
  },
  icon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  button: {
    marginTop: theme.spacing(2),
    width: "100%",
  },
  progressContainer: {
    width: "100%",
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  progressBar: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  progressText: {
    marginTop: theme.spacing(1),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const CleanupActions = ({ loading, error, onAction, onActionComplete }) => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId || 1;

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [actionToExecute, setActionToExecute] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [currentActionId, setCurrentActionId] = useState(null);
  const [currentQueueId, setCurrentQueueId] = useState(null); // FASE 7: Para jobs en background
  const [progress, setProgress] = useState(null);

  // Usar useRef para mantener la referencia actualizada de currentActionId en los handlers
  const currentActionIdRef = useRef(null);
  const currentQueueIdRef = useRef(null); // FASE 7: Para jobs en background
  const pollingIntervalRef = useRef(null); // FASE 7: Para limpiar polling

  // Sincronizar refs con states
  useEffect(() => {
    currentActionIdRef.current = currentActionId;
  }, [currentActionId]);

  useEffect(() => {
    currentQueueIdRef.current = currentQueueId;
  }, [currentQueueId]);

  const actions = [
    {
      id: "binary-logs",
      title: "Limpiar Binary Logs",
      description: "Elimina binary logs antiguos de MySQL",
      icon: <StorageIcon className={classes.icon} />,
      color: "primary",
      requiresConfirm: true,
      confirmMessage:
        "¿Está seguro de que desea limpiar los binary logs? Esta acción liberará espacio en disco.",
    },
    {
      id: "optimize",
      title: "Optimizar Tablas",
      description: "Optimiza tablas MySQL fragmentadas",
      icon: <BuildIcon className={classes.icon} />,
      color: "primary",
      requiresConfirm: true,
      confirmMessage:
        "¿Está seguro de que desea optimizar las tablas? Esta operación puede tardar varios minutos.",
    },
    {
      id: "refresh",
      title: "Actualizar Estado",
      description: "Refresca el estado actual del sistema",
      icon: <RefreshIcon className={classes.icon} />,
      color: "default",
      requiresConfirm: false,
    },
    {
      id: "emergency",
      title: "Limpieza de Emergencia",
      description: "Limpieza profunda cuando el disco está crítico",
      icon: <EmergencyIcon className={classes.icon} />,
      color: "secondary",
      requiresConfirm: true,
      confirmMessage:
        "ADVERTENCIA: Esta acción realizará una limpieza agresiva. ¿Está seguro de continuar?",
    },
  ];

  const handleActionClick = (action) => {
    setActionToExecute(action);
    if (action.requiresConfirm) {
      setConfirmDialogOpen(true);
    } else {
      executeAction(action.id);
    }
  };

  // FASE 6: Escuchar eventos de progreso vía WebSocket
  // IMPORTANTE: Registrar listeners SIEMPRE, no solo cuando hay currentActionId
  // Esto asegura que no perdamos eventos que se emiten antes de establecer currentActionId
  useEffect(() => {
    if (!socketManager) {
      return;
    }

    try {
      const managedSocket = socketManager.getSocket(companyId);
      if (!managedSocket) {
        return;
      }

      const progressEvent = `company-${companyId}-vps-action-progress`;
      const completedEvent = `company-${companyId}-vps-action-completed`;

      const handleProgress = (data) => {
        // Usar ref para obtener el valor actualizado de currentActionId
        const actionId = currentActionIdRef.current;

        // Si hay currentActionId, solo procesar eventos de ese actionId
        // Si no hay currentActionId, procesar todos (por si se establece después)
        if (!actionId || (data && data.actionId === actionId)) {
          setProgress(data.progress);
        }
      };

      const handleCompleted = (data) => {
        // Usar ref para obtener el valor actualizado de currentActionId
        const actionId = currentActionIdRef.current;

        // Si hay currentActionId, solo procesar eventos de ese actionId
        if (!actionId || (data && data.actionId === actionId)) {
          setProgress((prev) => ({
            ...(prev || {}),
            status: "completed",
            percent: 100,
            message: "Acción completada exitosamente",
          }));
          // Esperar un momento antes de ocultar para que el usuario vea el 100%
          setTimeout(() => {
            setCurrentActionId(null);
            setExecuting(false);
            setProgress(null);
          }, 2000);
          onActionComplete?.();
          toast.success(`Acción completada: ${data.result?.message || "Éxito"}`);
        }
      };

      // IMPORTANTE: Usar ManagedSocket.on() directamente (como en Schedules/index.js)
      // ManagedSocket maneja automáticamente la reconexión y re-registro de listeners
      managedSocket.on(progressEvent, handleProgress);
      managedSocket.on(completedEvent, handleCompleted);

      return () => {
        // Limpiar listeners usando ManagedSocket.off() (como en Schedules/index.js)
        if (managedSocket && typeof managedSocket.off === "function") {
          managedSocket.off(progressEvent, handleProgress);
          managedSocket.off(completedEvent, handleCompleted);
        }
      };
    } catch (error) {
      // Solo loggear errores críticos
      if (process.env.NODE_ENV === "development") {
        console.error("[CleanupActions] Error configurando socket:", error);
      }
    }
  }, [socketManager, companyId, onActionComplete]); // Remover currentActionId de las dependencias para que los listeners se registren siempre

  // FASE 7: Función para hacer polling del estado de un job en background
  const pollJobStatus = useRef(async (queueId) => {
    try {
      const { data } = await api.get(`/vps/actions/job/${queueId}`);

      if (data.success && data.data) {
        const jobData = data.data;

        // Actualizar progreso si existe
        if (jobData.progress) {
          setProgress({
            current: jobData.progress.current || 0,
            total: jobData.progress.total || 0,
            percent: jobData.progress.percent || 0,
            currentTable: jobData.progress.currentTable || null,
            currentStep: jobData.progress.currentStep || null,
            status: jobData.progress.status || "processing",
            message: jobData.progress.message || null,
          });
        }

        // Si el job está completado o falló, detener polling
        if (jobData.status === "completed") {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          setProgress((prev) => ({
            ...(prev || {}),
            status: "completed",
            percent: 100,
            message: "Acción completada exitosamente",
          }));

          // Esperar un momento antes de ocultar
          setTimeout(() => {
            setCurrentQueueId(null);
            setExecuting(false);
            setProgress(null);
          }, 2000);

          onActionComplete?.();

          toast.success(`Acción completada: ${jobData.result?.message || "Éxito"}`);
        } else if (jobData.status === "failed") {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          setProgress((prev) => ({
            ...(prev || {}),
            status: "failed",
            message: jobData.errorMessage || "Error en la ejecución",
          }));

          setTimeout(() => {
            setCurrentQueueId(null);
            setExecuting(false);
            setProgress(null);
          }, 2000);

          toast.error(`Acción falló: ${jobData.errorMessage || "Error desconocido"}`);
        }
      }
    } catch (err) {
      console.error("Error consultando estado del job:", err);
    }
  });

  // FASE 7: Efecto para hacer polling cuando hay queueId
  useEffect(() => {
    if (!currentQueueId) return;

    // Hacer polling inmediato
    pollJobStatus.current(currentQueueId);

    // Configurar polling cada 2 segundos
    pollingIntervalRef.current = setInterval(() => {
      if (currentQueueIdRef.current) {
        pollJobStatus.current(currentQueueIdRef.current);
      }
    }, 2000);

    // Limpiar al desmontar o cuando cambie queueId
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [currentQueueId]);

  const executeAction = async (actionId) => {
    try {
      setExecuting(true);
      setProgress({
        current: 0,
        total: 0,
        percent: 0,
        status: "processing",
        message: "Iniciando acción...",
      });

      // FASE 7: Ejecutar acción y detectar si es background (queueId) o directa (actionId)
      const result = await onAction(actionId);
      const queueId = result?.data?.queueId || result?.queueId;

      if (queueId) {
        setCurrentQueueId(queueId);
        setCurrentActionId(null);
      } else {
        setCurrentActionId(null);
        setCurrentQueueId(null);
        setProgress(null);
        setExecuting(false);
        onActionComplete?.();
      }

      setConfirmDialogOpen(false);
      setActionToExecute(null);
    } catch (err) {
      toast.error(`Error ejecutando acción: ${err.response?.data?.error || err.message || "Error desconocido"}`);
      setCurrentActionId(null);
      setCurrentQueueId(null);
      setProgress(null);
      setExecuting(false);
    }
  };

  const handleConfirm = () => {
    if (actionToExecute) {
      executeAction(actionToExecute.id);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setActionToExecute(null);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* FASE 6/7: Mostrar progreso en tiempo real (directo o background) */}
      {executing && (currentActionId || currentQueueId) && (
        <Box
          className={classes.progressContainer}
          style={{ marginBottom: 16, border: "1px solid #e0e0e0" }}
        >
          <Typography variant="subtitle2" gutterBottom style={{ fontWeight: "bold" }}>
            {progress?.status === "completed" ? "Acción completada" : "Progreso de la acción"}
          </Typography>
          <LinearProgress
            variant={
              progress?.percent !== undefined && progress.percent > 0
                ? "determinate"
                : "indeterminate"
            }
            value={progress?.percent || 0}
            className={classes.progressBar}
            style={{ height: 8, borderRadius: 4 }}
          />
          <Typography variant="body2" className={classes.progressText} style={{ marginTop: 8 }}>
            {progress?.message ||
              (progress?.currentTable
                ? `Optimizando tabla: ${progress.currentTable} (${progress.current || 0}/${progress.total || 0})`
                : progress?.currentStep
                  ? `Paso: ${progress.currentStep} (${progress.current || 0}/${progress.total || 0})`
                  : executing
                    ? `Procesando... Por favor espere`
                    : `Procesando... (${progress?.current || 0}/${progress?.total || 0})`)}
          </Typography>
          {progress?.currentStep && (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginTop: 4 }}
            >
              Paso: {progress.currentStep}
            </Typography>
          )}
          {progress?.currentTable && (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginTop: 4 }}
            >
              Tabla actual: <strong>{progress.currentTable}</strong>
            </Typography>
          )}
          {progress?.percent !== undefined && progress.percent > 0 && (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginTop: 4 }}
            >
              <strong>{progress.percent}%</strong> completado ({progress.current || 0} de{" "}
              {progress.total || 0})
            </Typography>
          )}
          {!progress && executing && (
            <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              style={{ marginTop: 4 }}
            >
              Esperando actualización de progreso...
            </Typography>
          )}
        </Box>
      )}

      <Grid container spacing={3}>
        {actions.map((action) => (
          <Grid item xs={12} sm={6} md={3} key={action.id}>
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                {action.icon}
                <Typography variant="h6" gutterBottom>
                  {action.title}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {action.description}
                </Typography>
                <Button
                  variant="contained"
                  color={action.color}
                  className={classes.button}
                  onClick={() => handleActionClick(action)}
                  disabled={executing || loading}
                  startIcon={
                    executing && actionToExecute?.id === action.id ? (
                      <CircularProgress size={20} />
                    ) : null
                  }
                >
                  {executing && actionToExecute?.id === action.id ? "Ejecutando..." : "Ejecutar"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Dialog de confirmación */}
      <Dialog open={confirmDialogOpen} onClose={handleCancel}>
        <DialogTitle>Confirmar Acción</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionToExecute?.confirmMessage || "¿Está seguro de continuar?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary" disabled={executing}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            color={actionToExecute?.color || "primary"}
            variant="contained"
            disabled={executing}
          >
            {executing ? <CircularProgress size={20} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CleanupActions;
